import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../../utils/structuredData';
import './ServicePage.css';

const WebDevelopment = () => {
  const serviceStructuredData = serviceSchema(
    'Web Development',
    'Create stunning, responsive websites and web applications. From simple landing pages to complex enterprise solutions, we bring your vision to life.',
    'Web Development'
  );

  const breadcrumbStructuredData = breadcrumbSchema([
    { name: 'Home', url: 'https://www.gettechupdate.com' },
    { name: 'Services', url: 'https://www.gettechupdate.com/#services' },
    { name: 'Web Development', url: 'https://www.gettechupdate.com/services/web-development' }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEO
        title="Website Development in Budget | Bhopal, Indore, Delhi, Mumbai | Tech Update"
        description="Affordable website development services in Bhopal, Indore, Delhi, Mumbai, Bangalore, and across India. Professional web development with React, Next.js, Node.js at budget-friendly prices. Responsive websites and web applications."
        keywords="website development in budget, web development in Bhopal, web development in Indore, website development in Delhi, website development in Mumbai, website development in Bangalore, affordable web development, budget website development, web development company in Bhopal, web development company in Indore, React development, Next.js, Node.js development, responsive web design, frontend development, backend development, web application development, website development services"
        url="https://www.gettechupdate.com/services/web-development"
        structuredData={combinedSchema}
      />
      <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-icon-large">
            <i className="fas fa-globe"></i>
          </div>
          <h1>Web Development</h1>
          <p className="service-hero-subtitle">
            Create stunning, responsive websites and web applications that engage users and drive business results.
          </p>
        </div>
      </div>

      <div className="service-content">
        <section className="service-section">
          <h2>Comprehensive Web Development Solutions</h2>
          <p>
            From simple landing pages to complex enterprise web applications, we deliver high-quality web solutions 
            that are fast, secure, and scalable. Our team specializes in modern web technologies to create exceptional 
            digital experiences that convert visitors into customers. We offer affordable website development services 
            in Bhopal, Indore, Delhi, Mumbai, Bangalore, Pune, Hyderabad, Chennai, Kolkata, Jaipur, and across India 
            at budget-friendly prices.
          </p>
        </section>

        <section className="service-section">
          <h2>Our Web Development Services</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-laptop-code"></i>
              <h3>Frontend Development</h3>
              <p>Beautiful, responsive user interfaces built with React, Next.js, and modern CSS frameworks for optimal user experience.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-server"></i>
              <h3>Backend Development</h3>
              <p>Robust server-side solutions using Node.js, Express, and databases to power your web applications.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-mobile-alt"></i>
              <h3>Responsive Design</h3>
              <p>Mobile-first approach ensuring your website looks and works perfectly on all devices and screen sizes.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-search"></i>
              <h3>SEO Optimization</h3>
              <p>Search engine optimized websites that rank higher and attract organic traffic to your business.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-tachometer-alt"></i>
              <h3>Performance Optimization</h3>
              <p>Fast-loading websites with optimized code, images, and caching strategies for superior performance.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shield-alt"></i>
              <h3>Security & SSL</h3>
              <p>Secure websites with SSL certificates, data encryption, and protection against common vulnerabilities.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Technologies We Use</h2>
          <div className="tech-stack">
            <div className="tech-item">
              <i className="fab fa-react"></i>
              <span>React</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-code"></i>
              <span>Next.js</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-node-js"></i>
              <span>Node.js</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>MongoDB</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>PostgreSQL</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-js"></i>
              <span>JavaScript</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-html5"></i>
              <span>HTML5</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-css3-alt"></i>
              <span>CSS3</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Types of Websites We Build</h2>
          <div className="app-types">
            <div className="app-type-card">
              <i className="fas fa-store"></i>
              <h3>E-commerce Websites</h3>
              <p>Full-featured online stores with shopping carts, payment gateways, inventory management, and order processing.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-briefcase"></i>
              <h3>Corporate Websites</h3>
              <p>Professional business websites showcasing your company, services, and establishing your online presence.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-blog"></i>
              <h3>Blog & Content Sites</h3>
              <p>Content management systems for blogs, news sites, and content-rich platforms with SEO optimization.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-chart-line"></i>
              <h3>Web Applications</h3>
              <p>Custom web apps for business processes, dashboards, CRM systems, and SaaS platforms.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-landmark"></i>
              <h3>Portfolio Websites</h3>
              <p>Stunning portfolio sites for creatives, agencies, and professionals to showcase their work.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-graduation-cap"></i>
              <h3>Educational Platforms</h3>
              <p>Learning management systems, online courses, and educational platforms with interactive features.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Development Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Requirements Analysis</h3>
              <p>We analyze your business needs, target audience, and goals to create a comprehensive project plan.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Design & Wireframing</h3>
              <p>Our designers create wireframes and mockups that align with your brand and user expectations.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Development</h3>
              <p>Agile development with regular updates, code reviews, and quality assurance throughout the process.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Testing & Optimization</h3>
              <p>Comprehensive testing across browsers and devices, performance optimization, and SEO implementation.</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Deployment</h3>
              <p>Secure deployment to production servers with proper configuration and monitoring setup.</p>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <h3>Maintenance & Support</h3>
              <p>Ongoing maintenance, updates, security patches, and technical support to keep your site running smoothly.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Website Development in Major Cities</h2>
          <p>
            We provide professional website development services across India, with a strong presence in major cities:
          </p>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Bhopal</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Indore</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Delhi</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Mumbai</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Bangalore</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Pune</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Hyderabad</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Chennai</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Kolkata</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Jaipur</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Ahmedabad</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Website Development in Surat</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Key Features We Implement</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Responsive Design</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>SEO Optimization</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Fast Loading Speed</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Payment Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>User Authentication</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Content Management</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Analytics Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Social Media Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Multi-language Support</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Contact Forms</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Live Chat Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>SSL Security</span>
            </div>
          </div>
        </section>

        <section className="service-cta">
          <h2>Ready to Build Your Website?</h2>
          <p>Get affordable website development services in Bhopal, Indore, and across India</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get A Free Quote</Link>
            <Link to="/contact" className="btn-secondary">Schedule a Consultation</Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default WebDevelopment;

